<template>
  <div class="wg-verdict">
    <div class="vhead">
      <span class="vdot" :style="{ background: tierMeta.dot }"></span>
      <div class="vtxt">
        <div class="vtop">
          <div class="vh">{{ headline }}</div>
          <span class="vtier" :class="tier">{{ tierMeta.label }}</span>
        </div>
        <div class="vmeta">
          {{ wins.length }} {{ wins.length === 1 ? 'win' : 'wins' }} in {{ periodLabel }}<template v-if="country"> ·
          <span :title="countryFull || country">{{ country }}</span></template> ·
          measured from Google Search Console<template v-if="provisional"> ·
          still collecting data</template>
        </div>
      </div>
    </div>
    <ul v-if="wins.length" class="wg-vwins">
      <li v-for="(win, index) in wins" :key="win.candidate_id || index" :class="{ gold: index === 0 }">
        <span class="wi">
          <i class="material-icons-outlined">{{ iconFor(index) }}</i>
        </span>
        <p v-if="winTitle(win)"><b>{{ winTitle(win) }}</b> {{ win.description }}</p>
        <p v-else>{{ win.description }}</p>
      </li>
    </ul>
  </div>
</template>

<script>
const TIER_META = {
  strong: { dot: '#25A97A' },
  steady: { dot: '#25A97A', label: 'Steady growth' },
  flat: { dot: '#FFB95A', label: 'Holding steady' },
  // wins-only language: never a negative tier chip
  down: { dot: '#5A6E8C', label: 'Your results' },
}
const WIN_ICONS = ['emoji_events', 'trending_up', 'my_location']

export default {
  name: 'WinsVerdictCard',
  props: {
    headline: {
      type: String,
      default: '',
    },
    tier: {
      type: String,
      default: 'steady',
    },
    wins: {
      type: Array,
      default: () => [],
    },
    periodLabel: {
      type: String,
      default: '',
    },
    /** Reporting scope label (e.g. "Worldwide", "United States + Canada"). */
    country: {
      type: String,
      default: '',
    },
    /** Full country list for the hover tooltip. */
    countryFull: {
      type: String,
      default: '',
    },
    /** Open-period snapshot: numbers keep changing until the period closes. */
    provisional: {
      type: Boolean,
      default: false,
    },
    periodWord: {
      type: String,
      default: 'quarter',
    },
  },
  computed: {
    tierMeta () {
      const meta = TIER_META[this.tier] || TIER_META.steady
      const label = meta.label ||
        (this.tier === 'strong' ? `Strong ${this.periodWord}` : 'Steady growth')
      return { ...meta, label }
    },
  },
  methods: {
    /** Design pattern: bold teal lead phrase, lighter continuation. */
    winTitle (win) {
      const title = (win.title || '').trim()
      if (!title) return ''
      return /[.!?:]$/.test(title) ? title : title + '.'
    },
    iconFor (index) {
      return WIN_ICONS[index % WIN_ICONS.length]
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../Settings/wg-tokens.scss';

/* verdict */
.wg-verdict {
  background: #fff;
  border: 1px solid $wg-panel-line;
  border-radius: 12px;
  padding: 22px 24px;
  margin-bottom: 20px;

  .vhead {
    display: flex;
    gap: 14px;
    align-items: flex-start;
  }

  .vtxt {
    flex: 1;
    min-width: 0;
  }

  .vtop {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .vtier {
    font-size: 0.75rem;
    font-weight: 700;
    padding: 4px 11px;
    border-radius: 30px;
    white-space: nowrap;

    &.strong,
    &.steady {
      background: $pop-success-bg;
      color: #1c805d;
    }

    &.flat {
      background: $pop-warning-bg;
      color: #7d6f24;
    }

    &.down {
      background: $pop-box-bg;
      color: $pop-ink-muted;
    }
  }

  .vdot {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    flex: none;
    margin-top: 7px;
  }

  .vh {
    font-size: 1.35rem;
    font-weight: 700;
    color: $pop-teal;
    line-height: 1.25;
  }

  .vmeta {
    font-size: 0.8rem;
    color: $pop-ink-muted;
    margin-top: 5px;
  }
}

.wg-vwins {
  list-style: none;
  margin: 16px 0 0;
  padding: 14px 0 0;
  border-top: 1px solid $wg-panel-line;
  display: flex;
  flex-direction: column;
  gap: 11px;

  li {
    display: flex;
    gap: 12px;
    align-items: flex-start;

    .wi {
      color: $pop-success;
      flex: none;
      margin-top: 1px;
      display: flex;

      .material-icons,
      .material-icons-outlined {
        font-size: 1.05rem;
      }
    }

    &.gold .wi {
      color: #c99a2e;
    }

    p {
      margin: 0;
      font-size: 0.95rem;
      line-height: 1.5;
      color: #1c4b45;
      text-wrap: pretty;

      b {
        font-weight: 700;
        color: $pop-teal;
      }
    }
  }
}
</style>
